/**
 * JobTypeWizard — multi-step job-type CREATION wizard (DEC-170 steps 1, 2, 4,
 * 5, 7, 8), wired into JobTypesEditor.tsx (Plan 09.1-11) so Create opens this
 * wizard while Edit keeps opening the flat form.
 *
 * Shape: a plain React state machine over an array of step ids, exactly like
 * setup-wizard/steps.tsx's own pattern — no wizard/stepper library exists in
 * this codebase (09.1-RESEARCH Pitfall 6) and none is added here. ONE draft
 * object held in React.useState, ONE submit at the end.
 *
 * IMPORTANT — no import from JobTypesEditor.tsx: JobTypesEditor renders this
 * component, so an import in the other direction would be a circular
 * module dependency. Every style helper / enum constant this file needs is
 * either imported from a non-UI module (use-job-types.ts, form-bits.tsx) or
 * copied verbatim from JobTypesEditor.tsx's matching control per this plan's
 * explicit "same component or same copied markup" instruction (see
 * `requirementLevelField` below).
 *
 * DEC-170 steps 3 (Sale vs. Rental / return-expected) and 6 (safety /
 * compliance — SDS linkage, hazmat driver-cert gating, DOT shipping papers)
 * are explicitly DEFERRED — see the comments inside TANK_FORK_STEPS below and
 * 09.1-CONTEXT.md `<deferred>`. Do not build them here.
 *
 * D-13: LINE_ITEM job types can be CREATED here (the ticket_shape
 * discriminator is real and enforced by the server's partial CHECK), but
 * there is NO line-item capture UI and NO Run/Stop/LineItem structure in
 * this file — that model is out-of-scope backlog work for a later phase.
 */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';
import { parseDollarsToCents } from '@lodex-pro/shared/money';
import { fieldStyle, FormRow } from '../sites-operators/form-bits';
import { useHaulerOperators, type HaulerOperator } from '../sites-operators/hooks';
import {
  JOB_TYPES_QUERY_KEY,
  JOB_TYPES_ADMIN_QUERY_KEY,
  REQUIREMENT_LEVELS,
  REQUIREMENT_LABELS,
  PHOTO_REQUIREMENT_HELP,
  TANK_COUNT_MODES,
  TANK_COUNT_MODE_LABELS,
  BILLING_METHODS,
  CAPTURE_METHODS,
  COMPLETION_LOCATIONS,
  type JobType,
  type RequirementLevel,
  type TicketShape,
  type TankCountMode,
} from '../jobs/use-job-types';
import { jobTypeCustomersQueryKey } from '../jobs/use-job-type-customers';
import { useProducts, type Product } from '../jobs/use-products';
import { formatNumber } from '@lodex-pro/shared/number-format';

// ─── Local style helpers ────────────────────────────────────────────────────
// Duplicated (not imported) from JobTypesEditor.tsx's own local consts of the
// same names, specifically to avoid the circular import described above.
// Keep visually identical if JobTypesEditor.tsx's versions change.

const subText: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid var(--color-border-primary)',
  marginBottom: '6px',
};
const btn: React.CSSProperties = {
  fontSize: '13px',
  padding: '6px 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-primary)',
  background: 'transparent',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  ...btn,
  background: 'var(--color-accent-blue)',
  borderColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const sectionHeading: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '13px',
  marginTop: '8px',
  borderTop: '1px solid var(--color-border-primary)',
  paddingTop: '8px',
};
const errorStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--color-danger)',
};
const forkChoiceStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '14px',
  borderRadius: '12px',
  border: '1px solid var(--color-border-primary)',
  background: 'transparent',
  color: 'var(--color-text-primary)',
  textAlign: 'left',
  cursor: 'pointer',
};
const forkChoiceActiveStyle: React.CSSProperties = {
  ...forkChoiceStyle,
  border: '2px solid var(--color-accent-blue)',
  background: 'var(--color-bg-tertiary)',
};

/** Copied verbatim (same JSX shape) from JobTypesEditor.tsx's `levelSelect`
 *  control (Plan 09.1-08) per this plan's explicit "same component or same
 *  copied markup" instruction — the two surfaces must not visually drift.
 *  Kept as a plain function (not a component reused via import) solely to
 *  avoid the JobTypesEditor <-> JobTypeWizard circular import described in
 *  the file header. */
function requirementLevelField(
  id: string,
  label: string,
  value: RequirementLevel,
  onChange: (value: RequirementLevel) => void,
  help?: string,
): React.JSX.Element {
  return (
    <FormRow label={label} htmlFor={id}>
      <>
        <select
          id={id}
          value={value}
          onChange={(e): void => onChange(e.target.value as RequirementLevel)}
          style={fieldStyle}
        >
          {REQUIREMENT_LEVELS.map((lvl) => (
            <option key={lvl} value={lvl}>
              {REQUIREMENT_LABELS[lvl]}
            </option>
          ))}
        </select>
        {help !== undefined && <div style={subText}>{help}</div>}
      </>
    </FormRow>
  );
}

// ─── Draft shape ────────────────────────────────────────────────────────────

type TankRequirementField =
  | 'waterVolumeRequirement'
  | 'oilVolumeRequirement'
  | 'topGaugeRequirement'
  | 'bottomGaugeRequirement'
  | 'sealRequirement'
  | 'colorCutRequirement'
  | 'tankNameRequirement';

/** Every field the wizard's steps write, before the final POST body is
 *  assembled. Rate + tank-count-max held as input strings, matching
 *  JobTypesEditor's FormState (parse-on-submit). */
interface WizardDraft {
  code: string;
  label: string;
  defaultProductId: string | null;
  unitOfMeasureCode: string;
  customerScope: 'ALL' | 'SPECIFIC';
  customerIds: readonly string[];
  ticketShape: TicketShape;
  tankCountMode: TankCountMode;
  tankCountMax: string;
  waterVolumeRequirement: RequirementLevel;
  oilVolumeRequirement: RequirementLevel;
  topGaugeRequirement: RequirementLevel;
  bottomGaugeRequirement: RequirementLevel;
  sealRequirement: RequirementLevel;
  colorCutRequirement: RequirementLevel;
  tankNameRequirement: RequirementLevel;
  isServiceWork: boolean;
  billingMethod: string;
  rateDollars: string;
  useCompanyPricing: boolean;
  disposalFeeEnabled: boolean;
  driverCommentRequirement: RequirementLevel;
  photoRequirement: RequirementLevel;
  driverTimeRequirement: RequirementLevel;
  completionLocation: string;
  captureMethod: string;
  /** True once the tenant has manually edited ANY step-7 field. The smart-
   *  default effect below stops overwriting values once this flips —
   *  "pre-populate, never lock." */
  requirementsTouched: boolean;
}

const EMPTY_DRAFT: WizardDraft = {
  code: '',
  label: '',
  defaultProductId: null,
  unitOfMeasureCode: 'BBL',
  customerScope: 'ALL',
  customerIds: [],
  ticketShape: 'TANK',
  tankCountMode: 'SINGLE',
  tankCountMax: '',
  waterVolumeRequirement: 'OPTIONAL',
  oilVolumeRequirement: 'OPTIONAL',
  topGaugeRequirement: 'OPTIONAL',
  bottomGaugeRequirement: 'OPTIONAL',
  sealRequirement: 'OPTIONAL',
  colorCutRequirement: 'OPTIONAL',
  tankNameRequirement: 'OPTIONAL',
  isServiceWork: false,
  billingMethod: 'PER_BBL',
  rateDollars: '',
  useCompanyPricing: false,
  disposalFeeEnabled: false,
  driverCommentRequirement: 'OPTIONAL',
  photoRequirement: 'OPTIONAL',
  driverTimeRequirement: 'OPTIONAL',
  completionLocation: 'DISPOSAL_FACILITY',
  captureMethod: 'SINGLE_GAUGE',
  requirementsTouched: false,
};

type SetDraft = <K extends keyof WizardDraft>(key: K, value: WizardDraft[K]) => void;

// ─── Step routing (DEC-170 numbering preserved) ────────────────────────────

type StepId =
  | 'product'
  | 'customer'
  | 'fork'
  | 'tankCount'
  | 'fieldRequirements'
  | 'crossCutting'
  | 'review';

const TANK_FORK_STEPS: readonly StepId[] = [
  'product',
  'customer',
  'fork',
  // Deferred: step 3 (Sale vs. Rental / return-expected flag) would sit
  // here. Owner: the Tenant Self-Service Billing Rules Engine
  // (09.1-CONTEXT.md <deferred>). NOT built in this plan.
  'tankCount',
  // Deferred: step 6 (safety/compliance — SDS linkage, hazmat driver-cert
  // gating, DOT shipping papers) would sit here. Owner: unscoped, may
  // warrant its own phase (09.1-CONTEXT.md <deferred>). NOT built.
  'fieldRequirements',
  'crossCutting',
  'review',
];

const LINE_ITEM_FORK_STEPS: readonly StepId[] = [
  'product',
  'customer',
  'fork',
  'crossCutting',
  'review',
];

function activeStepsFor(ticketShape: TicketShape): readonly StepId[] {
  return ticketShape === 'TANK' ? TANK_FORK_STEPS : LINE_ITEM_FORK_STEPS;
}

const STEP_TITLES: Record<StepId, string> = {
  product: 'Step 1 — What are you hauling/delivering?',
  customer: 'Step 2 — Customer scope',
  fork: 'Step 4 — Gauge a tank, or report a quantity?',
  tankCount: 'Step 5 — Tank count limit',
  fieldRequirements: 'Step 7 — Field requirements',
  crossCutting: 'Step 8 — Billing, service & driver requirements',
  review: 'Review',
};

// ─── Smart defaults (DEC-170 step 7) ───────────────────────────────────────

type ProductCategory = 'WATER' | 'CHEMICAL' | 'UNKNOWN';

/** The `products` table (DEC-026) carries no dedicated classification
 *  column, so category is derived from the selected product's code/name
 *  text — a heuristic, not a stored field. Per the plan: "if the catalog
 *  carries no usable classification, fall back to the water/produced-fluid
 *  defaults (the platform's core case)." */
function deriveProductCategory(product: Product | undefined): ProductCategory {
  if (product === undefined) return 'UNKNOWN';
  const hay = `${product.code} ${product.name}`.toUpperCase();
  if (/CHEM|SOLID|EQUIP|SAND|FUEL/.test(hay)) return 'CHEMICAL';
  if (/WATER|OIL|PRODUCED|FLOWBACK|SKIM|CRUDE/.test(hay)) return 'WATER';
  return 'UNKNOWN';
}

function smartDefaults(
  category: ProductCategory,
  isServiceWork: boolean,
): Record<TankRequirementField, RequirementLevel> {
  if (isServiceWork) {
    return {
      waterVolumeRequirement: 'OPTIONAL',
      oilVolumeRequirement: 'OPTIONAL',
      topGaugeRequirement: 'HIDDEN',
      bottomGaugeRequirement: 'HIDDEN',
      sealRequirement: 'OPTIONAL',
      colorCutRequirement: 'OPTIONAL',
      tankNameRequirement: 'OPTIONAL',
    };
  }
  if (category === 'CHEMICAL') {
    return {
      waterVolumeRequirement: 'OPTIONAL',
      oilVolumeRequirement: 'OPTIONAL',
      topGaugeRequirement: 'HIDDEN',
      bottomGaugeRequirement: 'HIDDEN',
      sealRequirement: 'OPTIONAL',
      colorCutRequirement: 'HIDDEN',
      tankNameRequirement: 'OPTIONAL',
    };
  }
  // WATER, or UNKNOWN falling back to the platform's core produced-water case.
  return {
    waterVolumeRequirement: 'REQUIRED',
    oilVolumeRequirement: 'OPTIONAL',
    topGaugeRequirement: 'REQUIRED',
    bottomGaugeRequirement: 'REQUIRED',
    sealRequirement: 'OPTIONAL',
    colorCutRequirement: 'OPTIONAL',
    tankNameRequirement: 'OPTIONAL',
  };
}

// ─── Submit body ────────────────────────────────────────────────────────────

/** Serialize the draft into POST /v1/job-types's body (createBodyBase,
 *  packages/api/src/routes/job-types/list.ts). Mirrors JobTypesEditor's
 *  toBody() shape for the fields both surfaces share. */
function toCreateBody(d: WizardDraft): Record<string, unknown> {
  const isTank = d.ticketShape === 'TANK';
  const trimmedRate = d.rateDollars.trim();
  // See JobTypesEditor.toBody — same unguarded free-text rate field, same cent lost
  // on `1.005`. Both surfaces now go through the one exact helper.
  const parsedRate = parseDollarsToCents(trimmedRate);
  const rateCents = parsedRate.ok ? parsedRate.cents : null;
  const trimmedMax = d.tankCountMax.trim();
  const tankCountMax =
    isTank && d.tankCountMode === 'MAX_N' && trimmedMax !== ''
      ? Number.parseInt(trimmedMax, 10)
      : null;

  return {
    code: d.code.trim().toUpperCase(),
    label: d.label.trim(),
    billingMethod: d.billingMethod,
    defaultRateCents: rateCents,
    unitOfMeasureCode: d.unitOfMeasureCode.trim() === '' ? null : d.unitOfMeasureCode.trim(),
    // captureMethod (Single Gauge / Before-After) is tank gauge methodology — it has no meaning
    // for a LINE_ITEM job type, so gate it on isTank like the tank fields below. It can't send
    // NULL (capture_method is NOT NULL), so a non-TANK shape sends the inert schema default
    // 'SINGLE_GAUGE' — nothing reads it (Line-Item capture path is out of scope, D-13).
    captureMethod: isTank ? d.captureMethod : 'SINGLE_GAUGE',
    completionLocation: d.completionLocation,
    // D-05 — the toggle IS the control; captureType is never rendered raw.
    captureType: d.isServiceWork ? 'SERVICE_CALL' : 'HAUL',
    ticketShape: d.ticketShape,
    // D-03 — LINE_ITEM must carry NO tank-only field; the server CHECK
    // requires every tank column to be NULL when ticketShape is LINE_ITEM.
    tankCountMode: isTank ? d.tankCountMode : null,
    tankCountMax: isTank ? tankCountMax : null,
    waterVolumeRequirement: isTank ? d.waterVolumeRequirement : null,
    oilVolumeRequirement: isTank ? d.oilVolumeRequirement : null,
    topGaugeRequirement: isTank ? d.topGaugeRequirement : null,
    bottomGaugeRequirement: isTank ? d.bottomGaugeRequirement : null,
    sealRequirement: isTank ? d.sealRequirement : null,
    colorCutRequirement: isTank ? d.colorCutRequirement : null,
    tankNameRequirement: isTank ? d.tankNameRequirement : null,
    driverCommentRequirement: d.driverCommentRequirement,
    photoRequirement: d.photoRequirement,
    driverTimeRequirement: d.driverTimeRequirement,
    useCompanyPricing: d.useCompanyPricing,
    disposalFeeEnabled: d.disposalFeeEnabled,
    // NOTE (known gap, see 09.1-11-SUMMARY.md): defaultProductId is
    // intentionally NOT sent. POST /v1/job-types's locked contract
    // (createBodyBase in the file above) does not accept it, even though
    // job_types.default_product_id exists as a column (DEC-026). The
    // product selected in step 1 is used ONLY to derive this wizard's
    // step-7 smart defaults, client-side.
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

export interface JobTypeWizardProps {
  readonly onCreated: (jobType: JobType) => void;
  readonly onCancel: () => void;
}

export function JobTypeWizard({ onCreated, onCancel }: JobTypeWizardProps): React.JSX.Element {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const { data: productsData, isLoading: productsLoading } = useProducts();
  const { data: haulerData } = useHaulerOperators();
  const products = productsData?.products ?? [];
  const haulerOperators = haulerData?.haulerOperators ?? [];

  const [draft, setDraft] = React.useState<WizardDraft>(EMPTY_DRAFT);
  const [stepId, setStepId] = React.useState<StepId>('product');
  const [stepError, setStepError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [createdJobType, setCreatedJobType] = React.useState<JobType | null>(null);
  const [scopingRetryNeeded, setScopingRetryNeeded] = React.useState(false);

  const activeSteps = activeStepsFor(draft.ticketShape);
  // If the current fork no longer has this step (e.g. switched to LINE_ITEM
  // while on 'tankCount'), fall back onto 'fork' instead of rendering a step
  // that no longer applies to the current shape.
  const currentStepId = activeSteps.includes(stepId) ? stepId : 'fork';
  const currentIndex = activeSteps.indexOf(currentStepId);

  const set: SetDraft = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  // Smart defaults (DEC-170 step 7): recompute from the selected product +
  // Service Work whenever either changes, but ONLY while the tenant hasn't
  // manually touched a step-7 field yet — pre-populate, never lock. Service
  // Work is answered on step 8 (AFTER step 7 in the tank-fork order), so
  // this also re-applies if the tenant sets Service Work then navigates
  // back to step 7 without having touched it there yet.
  React.useEffect(() => {
    if (draft.requirementsTouched) return;
    const product = products.find((p) => p.id === draft.defaultProductId);
    const category = deriveProductCategory(product);
    const defaults = smartDefaults(category, draft.isServiceWork);
    setDraft((prev) => ({ ...prev, ...defaults }));
    // Keying ONLY on the two inputs that actually drive the derivation
    // (plus the touched guard) — `products` array identity churns on every
    // background refetch and would otherwise re-run this needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.defaultProductId, draft.isServiceWork, draft.requirementsTouched]);

  const touchRequirement = (key: TankRequirementField, value: RequirementLevel): void => {
    setDraft((prev) => ({ ...prev, [key]: value, requirementsTouched: true }));
  };

  const gaugeCoherenceError = (): string | null => {
    if (
      draft.ticketShape === 'TANK' &&
      draft.bottomGaugeRequirement === 'REQUIRED' &&
      draft.topGaugeRequirement !== 'REQUIRED'
    ) {
      return "Bottom gauge cannot be required unless the top gauge is — gauging an empty tank's bottom is meaningless without the full-tank top reading.";
    }
    return null;
  };

  const validateStep = (id: StepId): string | null => {
    switch (id) {
      case 'product':
        if (draft.label.trim() === '') return 'Label is required.';
        if (!/^[A-Z0-9_]{2,40}$/.test(draft.code.trim().toUpperCase())) {
          return 'Code must be 2–40 chars: A–Z, 0–9, underscore.';
        }
        return null;
      case 'customer':
        if (draft.customerScope === 'SPECIFIC' && draft.customerIds.length === 0) {
          return 'Select at least one customer, or choose All customers.';
        }
        return null;
      case 'fork':
        return null;
      case 'tankCount':
        if (draft.tankCountMode === 'MAX_N') {
          if (draft.tankCountMax.trim() === '') return 'Enter a max tank count.';
          if (Number.parseInt(draft.tankCountMax, 10) < 2)
            return 'Max tank count must be 2 or more.';
        }
        return null;
      case 'fieldRequirements':
        return gaugeCoherenceError();
      case 'crossCutting':
      case 'review':
        return null;
    }
  };

  const goNext = (): void => {
    const err = validateStep(currentStepId);
    if (err !== null) {
      setStepError(err);
      return;
    }
    setStepError(null);
    const next = activeSteps[currentIndex + 1];
    if (next !== undefined) setStepId(next);
  };
  const goBack = (): void => {
    setStepError(null);
    const prev = activeSteps[currentIndex - 1];
    if (prev !== undefined) setStepId(prev);
  };
  const goTo = (id: StepId): void => {
    setStepError(null);
    setStepId(id);
  };

  const invalidateAfterCreate = async (jobTypeId: number): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: JOB_TYPES_QUERY_KEY }),
      qc.invalidateQueries({ queryKey: JOB_TYPES_ADMIN_QUERY_KEY }),
      // Prefix invalidation — covers every useJobTypesForCustomer(...) key.
      qc.invalidateQueries({ queryKey: ['job-types', 'catalog', 'customer'] }),
      qc.invalidateQueries({ queryKey: jobTypeCustomersQueryKey(jobTypeId) }),
    ]);
  };

  const applyCustomerScope = async (jobTypeId: number): Promise<boolean> => {
    try {
      const res = await authedFetch(opts, `/v1/job-types/${String(jobTypeId)}/customers`, {
        method: 'PUT',
        body: JSON.stringify({ customerIds: draft.customerIds }),
      });
      return res.ok;
    } catch {
      return false;
    }
  };

  const submit = async (): Promise<void> => {
    const err = gaugeCoherenceError();
    if (err !== null) {
      setStepError(err);
      setStepId('fieldRequirements');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await authedFetch(opts, '/v1/job-types', {
        method: 'POST',
        body: JSON.stringify(toCreateBody(draft)),
      });
      if (!res.ok) {
        setSubmitError(
          res.status === 400
            ? 'Check your inputs — a job type with this code may already exist.'
            : `Create failed (${String(res.status)}).`,
        );
        return;
      }
      const resBody = (await res.json()) as { jobType: JobType };
      const jt = resBody.jobType;
      setCreatedJobType(jt);
      if (draft.customerScope === 'SPECIFIC') {
        const ok = await applyCustomerScope(jt.id);
        if (!ok) {
          // T-0911-03 — never report success on a partially-applied create;
          // the job type exists (unscoped/all-customers) but scoping didn't.
          setScopingRetryNeeded(true);
          setSubmitError(
            'Job type created, but customer scoping failed to save. The job type itself was NOT lost — retry scoping below.',
          );
          return;
        }
      }
      await invalidateAfterCreate(jt.id);
      onCreated(jt);
    } catch {
      setSubmitError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const retryScoping = async (): Promise<void> => {
    if (createdJobType === null) return;
    setSubmitting(true);
    const ok = await applyCustomerScope(createdJobType.id);
    setSubmitting(false);
    if (!ok) {
      setSubmitError(
        'Customer scoping failed again. Set customer scope later from the flat edit form.',
      );
      return;
    }
    await invalidateAfterCreate(createdJobType.id);
    setSubmitError(null);
    setScopingRetryNeeded(false);
    onCreated(createdJobType);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '12px',
        marginTop: '10px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--color-bg-secondary)',
        maxWidth: '560px',
      }}
    >
      <div style={{ fontWeight: 600 }}>New job type</div>
      <div style={subText}>
        Step {currentIndex + 1} of {activeSteps.length}
      </div>
      <div style={sectionHeading}>{STEP_TITLES[currentStepId]}</div>

      {currentStepId === 'product' && (
        <ProductStep
          draft={draft}
          set={set}
          products={products}
          productsLoading={productsLoading}
        />
      )}
      {currentStepId === 'customer' && (
        <CustomerScopeStep draft={draft} set={set} haulerOperators={haulerOperators} />
      )}
      {currentStepId === 'fork' && <ForkStep draft={draft} set={set} />}
      {currentStepId === 'tankCount' && <TankCountStep draft={draft} set={set} />}
      {currentStepId === 'fieldRequirements' && (
        <FieldRequirementsStep draft={draft} touchRequirement={touchRequirement} />
      )}
      {currentStepId === 'crossCutting' && <CrossCuttingStep draft={draft} set={set} />}
      {currentStepId === 'review' && (
        <ReviewStep
          draft={draft}
          products={products}
          haulerOperators={haulerOperators}
          goTo={goTo}
        />
      )}

      {stepError !== null && <div style={errorStyle}>{stepError}</div>}
      {submitError !== null && (
        <div style={errorStyle}>
          {submitError}
          {scopingRetryNeeded && (
            <div style={{ marginTop: '6px' }}>
              <button
                type="button"
                style={btn}
                disabled={submitting}
                onClick={(): void => void retryScoping()}
              >
                {submitting ? 'Retrying…' : 'Retry customer scoping'}
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        {currentIndex > 0 && (
          <button type="button" style={btn} disabled={submitting} onClick={goBack}>
            Back
          </button>
        )}
        {currentStepId !== 'review' && (
          <button type="button" style={primaryBtn} disabled={submitting} onClick={goNext}>
            Next
          </button>
        )}
        {currentStepId === 'review' && (
          <button
            type="button"
            style={primaryBtn}
            disabled={submitting || scopingRetryNeeded}
            onClick={(): void => void submit()}
          >
            {submitting ? 'Creating…' : 'Create job type'}
          </button>
        )}
        <button type="button" style={btn} disabled={submitting} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Step components ────────────────────────────────────────────────────────

function ProductStep({
  draft,
  set,
  products,
  productsLoading,
}: {
  draft: WizardDraft;
  set: SetDraft;
  products: readonly Product[];
  productsLoading: boolean;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <FormRow label="Code" htmlFor="wiz-code" required>
        <input
          id="wiz-code"
          value={draft.code}
          placeholder="PRODUCED_WATER_HAUL"
          onChange={(e): void => set('code', e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Label" htmlFor="wiz-label" required>
        <input
          id="wiz-label"
          value={draft.label}
          onChange={(e): void => set('label', e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Product" htmlFor="wiz-product">
        <>
          <select
            id="wiz-product"
            value={draft.defaultProductId ?? ''}
            onChange={(e): void => {
              const id = e.target.value === '' ? null : e.target.value;
              set('defaultProductId', id);
              const picked = products.find((p) => p.id === id);
              if (picked !== undefined) set('unitOfMeasureCode', picked.unitOfMeasure);
            }}
            style={fieldStyle}
          >
            <option value="">Select a product…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <div style={subText}>
            {productsLoading
              ? 'Loading products…'
              : 'Drives the step 7 field-requirement defaults below (DEC-026). Not yet persisted as a saved job-type default — see the plan SUMMARY.'}
          </div>
        </>
      </FormRow>
      <FormRow label="Unit of measure" htmlFor="wiz-uom">
        <input
          id="wiz-uom"
          value={draft.unitOfMeasureCode}
          placeholder="BBL"
          onChange={(e): void => set('unitOfMeasureCode', e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
    </div>
  );
}

function CustomerScopeStep({
  draft,
  set,
  haulerOperators,
}: {
  draft: WizardDraft;
  set: SetDraft;
  haulerOperators: readonly HaulerOperator[];
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
        <input
          type="radio"
          name="wiz-scope"
          checked={draft.customerScope === 'ALL'}
          onChange={(): void => set('customerScope', 'ALL')}
        />
        All customers
      </label>
      <div style={{ ...subText, marginLeft: '22px' }}>
        Default — stored as the empty-set sentinel. Every customer sees this job type.
      </div>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
        <input
          type="radio"
          name="wiz-scope"
          checked={draft.customerScope === 'SPECIFIC'}
          onChange={(): void => set('customerScope', 'SPECIFIC')}
        />
        Specific customers
      </label>
      {draft.customerScope === 'SPECIFIC' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            maxHeight: '160px',
            overflowY: 'auto',
            marginLeft: '22px',
          }}
        >
          {haulerOperators.map((op) => (
            <label
              key={op.id}
              style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}
            >
              <input
                type="checkbox"
                checked={draft.customerIds.includes(op.id)}
                onChange={(): void => {
                  const next = draft.customerIds.includes(op.id)
                    ? draft.customerIds.filter((x) => x !== op.id)
                    : [...draft.customerIds, op.id];
                  set('customerIds', next);
                }}
              />
              {op.resolvedName}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function ForkStep({ draft, set }: { draft: WizardDraft; set: SetDraft }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={subText}>Structural — changing this later creates a new version.</div>
      <button
        type="button"
        style={draft.ticketShape === 'TANK' ? forkChoiceActiveStyle : forkChoiceStyle}
        onClick={(): void => set('ticketShape', 'TANK')}
      >
        <span style={{ fontWeight: 600 }}>Gauge a tank</span>
        <span style={subText}>Dynamic tank list — gauges, volumes, seal, color cut per tank.</span>
      </button>
      <button
        type="button"
        style={draft.ticketShape === 'LINE_ITEM' ? forkChoiceActiveStyle : forkChoiceStyle}
        onClick={(): void => {
          // Phase 12.1 (D-06) — a LINE_ITEM job type ALWAYS bills PER_UNIT.
          // migration 0075's job_types_line_item_billing_method_check REJECTS
          // any LINE_ITEM row whose billing_method <> 'PER_UNIT', so lock the
          // field at the source rather than let the dispatcher build a
          // combination the DB will bounce.
          set('ticketShape', 'LINE_ITEM');
          set('billingMethod', 'PER_UNIT');
        }}
      >
        <span style={{ fontWeight: 600 }}>Report a quantity / count units</span>
        <span style={subText}>Catalog product plus a quantity, not a tank gauge.</span>
      </button>
      {draft.ticketShape === 'LINE_ITEM' && (
        <div style={subText}>
          Line-item job types can be created and configured now. The line-item capture screen for
          drivers ships in a later phase.
        </div>
      )}
    </div>
  );
}

function TankCountStep({ draft, set }: { draft: WizardDraft; set: SetDraft }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={subText}>Structural — changing this later creates a new version.</div>
      <FormRow label="Tank count mode" htmlFor="wiz-tank-count-mode">
        <select
          id="wiz-tank-count-mode"
          value={draft.tankCountMode}
          onChange={(e): void => set('tankCountMode', e.target.value as TankCountMode)}
          style={fieldStyle}
        >
          {TANK_COUNT_MODES.map((m) => (
            <option key={m} value={m}>
              {TANK_COUNT_MODE_LABELS[m]}
            </option>
          ))}
        </select>
      </FormRow>
      {draft.tankCountMode === 'MAX_N' && (
        <FormRow label="Max tank count" htmlFor="wiz-tank-count-max" required>
          <input
            id="wiz-tank-count-max"
            inputMode="numeric"
            value={draft.tankCountMax}
            placeholder="e.g. 4"
            onChange={(e): void => set('tankCountMax', e.target.value)}
            style={fieldStyle}
          />
        </FormRow>
      )}
    </div>
  );
}

function FieldRequirementsStep({
  draft,
  touchRequirement,
}: {
  draft: WizardDraft;
  touchRequirement: (key: TankRequirementField, value: RequirementLevel) => void;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={subText}>
        Pre-populated from the product you picked in step 1 — every default below is editable.
      </div>
      {requirementLevelField('wiz-top-gauge', 'Top gauge', draft.topGaugeRequirement, (v) =>
        touchRequirement('topGaugeRequirement', v),
      )}
      {requirementLevelField(
        'wiz-bottom-gauge',
        'Bottom gauge',
        draft.bottomGaugeRequirement,
        (v) => touchRequirement('bottomGaugeRequirement', v),
      )}
      {requirementLevelField(
        'wiz-water-volume',
        'Water volume',
        draft.waterVolumeRequirement,
        (v) => touchRequirement('waterVolumeRequirement', v),
      )}
      {requirementLevelField('wiz-oil-volume', 'Oil volume', draft.oilVolumeRequirement, (v) =>
        touchRequirement('oilVolumeRequirement', v),
      )}
      {requirementLevelField('wiz-seal', 'Seal info', draft.sealRequirement, (v) =>
        touchRequirement('sealRequirement', v),
      )}
      {requirementLevelField(
        'wiz-color-cut',
        'Color cut',
        draft.colorCutRequirement,
        (v) => touchRequirement('colorCutRequirement', v),
        'Predefined option list is still pending real hauler input (OPEN-001) — only the mechanism ships here.',
      )}
      {requirementLevelField(
        'wiz-tank-name',
        'Tank name',
        draft.tankNameRequirement,
        (v) => touchRequirement('tankNameRequirement', v),
        "Auto-filled from a registered tank when linked; otherwise the driver's entry; otherwise Tank 1, Tank 2, …",
      )}
    </div>
  );
}

function CrossCuttingStep({
  draft,
  set,
}: {
  draft: WizardDraft;
  set: SetDraft;
}): React.JSX.Element {
  const companyPricingHelp =
    "Uses this customer's rate when one is set; otherwise falls back to the rate above.";
  const disposalFeeHelp = 'Fee amounts are configured in Billing.';
  const driverTimeHelp =
    'Total time is confirmed by the driver at completion; elapsed time is derived from the timeclock event chain.';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
        <input
          type="checkbox"
          checked={draft.isServiceWork}
          onChange={(e): void => set('isServiceWork', e.target.checked)}
        />
        Service Work?
      </label>

      <FormRow label="Billing method" htmlFor="wiz-billing">
        {draft.ticketShape === 'LINE_ITEM' ? (
          // Phase 12.1 (D-06) — LINE_ITEM always bills PER_UNIT (locked by
          // migration 0075's DB CHECK); render read-only, not an editable
          // dropdown, so the invalid combination can't be constructed.
          <div style={fieldStyle}>
            <span>PER_UNIT</span>
            <span style={{ ...subText, marginLeft: '8px' }}>
              (Line-item job types always bill per unit)
            </span>
          </div>
        ) : (
          <select
            id="wiz-billing"
            value={draft.billingMethod}
            onChange={(e): void => set('billingMethod', e.target.value)}
            style={fieldStyle}
          >
            {BILLING_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        )}
      </FormRow>
      <FormRow label="Standard rate ($)" htmlFor="wiz-rate">
        <input
          id="wiz-rate"
          inputMode="decimal"
          value={draft.rateDollars}
          placeholder="optional"
          onChange={(e): void => set('rateDollars', e.target.value)}
          style={fieldStyle}
        />
        <div style={subText}>
          Used when the customer has no price for this job type and no general rate.
        </div>
      </FormRow>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
        <input
          type="checkbox"
          checked={draft.useCompanyPricing}
          onChange={(e): void => set('useCompanyPricing', e.target.checked)}
        />
        Use company pricing
      </label>
      <div style={{ ...subText, marginLeft: '24px' }}>{companyPricingHelp}</div>
      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
        <input
          type="checkbox"
          checked={draft.disposalFeeEnabled}
          onChange={(e): void => set('disposalFeeEnabled', e.target.checked)}
        />
        Add a disposal fee when billing
      </label>
      <div style={{ ...subText, marginLeft: '24px' }}>{disposalFeeHelp}</div>

      {requirementLevelField(
        'wiz-driver-comment',
        'Driver comment',
        draft.driverCommentRequirement,
        (v) => set('driverCommentRequirement', v),
      )}
      {requirementLevelField(
        'wiz-photo',
        'Photo',
        draft.photoRequirement,
        (v) => set('photoRequirement', v),
        PHOTO_REQUIREMENT_HELP,
      )}
      {requirementLevelField(
        'wiz-driver-time',
        'Driver time',
        draft.driverTimeRequirement,
        (v) => set('driverTimeRequirement', v),
        driverTimeHelp,
      )}

      <FormRow label="Completion location" htmlFor="wiz-completion">
        <select
          id="wiz-completion"
          value={draft.completionLocation}
          onChange={(e): void => set('completionLocation', e.target.value)}
          style={fieldStyle}
        >
          {COMPLETION_LOCATIONS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </FormRow>
      {/* Capture method is tank gauge methodology (Single Gauge / Before-After) — hide it for
          LINE_ITEM shapes, matching the isTank gating of the other tank-only fields. */}
      {draft.ticketShape === 'TANK' && (
        <FormRow label="Capture method" htmlFor="wiz-capture">
          <select
            id="wiz-capture"
            value={draft.captureMethod}
            onChange={(e): void => set('captureMethod', e.target.value)}
            style={fieldStyle}
          >
            {CAPTURE_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </FormRow>
      )}
    </div>
  );
}

function ReviewStep({
  draft,
  products,
  haulerOperators,
  goTo,
}: {
  draft: WizardDraft;
  products: readonly Product[];
  haulerOperators: readonly HaulerOperator[];
  goTo: (id: StepId) => void;
}): React.JSX.Element {
  const product = products.find((p) => p.id === draft.defaultProductId);
  const scopedNames = draft.customerIds
    .map((id) => haulerOperators.find((op) => op.id === id)?.resolvedName ?? id)
    .join(', ');

  const line = (label: string, value: string, target: StepId): React.JSX.Element => (
    <div key={label} style={rowStyle}>
      <span style={{ flex: 1 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <div style={subText}>{value}</div>
      </span>
      <button type="button" style={btn} onClick={(): void => goTo(target)}>
        Back to step
      </button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {line(
        'Identity',
        `${draft.code.trim().toUpperCase() || '—'} · ${draft.label || '—'} · ${product?.name ?? 'No product selected'}`,
        'product',
      )}
      {line(
        'Customer scope',
        draft.customerScope === 'ALL' ? 'All customers' : scopedNames || 'No customers selected',
        'customer',
      )}
      {line(
        'Structure',
        draft.ticketShape === 'TANK' ? 'Gauge a tank' : 'Report a quantity / count units',
        'fork',
      )}
      {draft.ticketShape === 'TANK' &&
        line(
          'Tank count',
          `${TANK_COUNT_MODE_LABELS[draft.tankCountMode]}${
            draft.tankCountMode === 'MAX_N' ? ` (max ${formatNumber(draft.tankCountMax)})` : ''
          }`,
          'tankCount',
        )}
      {draft.ticketShape === 'TANK' &&
        line(
          'Field requirements',
          `Top ${draft.topGaugeRequirement} · Bottom ${draft.bottomGaugeRequirement} · Water ${draft.waterVolumeRequirement} · Oil ${draft.oilVolumeRequirement} · Seal ${draft.sealRequirement} · Color cut ${draft.colorCutRequirement} · Tank name ${draft.tankNameRequirement}`,
          'fieldRequirements',
        )}
      {line(
        'Billing & service',
        `${draft.isServiceWork ? 'Service Work' : 'Haul'} · ${draft.billingMethod}${
          draft.rateDollars.trim() !== '' ? ` · $${draft.rateDollars}` : ''
        } · ${draft.completionLocation}`,
        'crossCutting',
      )}
    </div>
  );
}
